import { useCallback, useEffect, useRef, useState } from "react";
import { organizationApi } from "../services/apis/organization.api";
import type { OrganizationLanguages } from "../services/apis/organization.api";

/** One organisation's read, as `useOrganizationLanguagesRead` gives it. */
export interface OrganizationLanguagesRead {
    /** The whole answer, or null while it is on its way and when it could not be read. */
    settings: OrganizationLanguages | null;
    /** The last read for this organisation failed, and none has been asked again since. */
    failed: boolean;
    /** Asks again (a screen's "Retry"); asks nothing without an organisation. */
    retry: () => void;
}

/** What has been read, and for which organisation. */
interface Read {
    organizationId: string;
    settings: OrganizationLanguages | null;
    failed: boolean;
}

/**
 * An organisation's language settings, read once for whoever needs them, with
 * whether the read failed and a way to ask again. See
 * `useOrganizationLanguages`, which is this without the failure.
 */
export function useOrganizationLanguagesRead(
    organizationId: string | null | undefined,
    header: any
): OrganizationLanguagesRead {
    const [read, setRead] = useState<Read | null>(null);
    // Bumped by a retry, so the same organisation is read again.
    const [attempt, setAttempt] = useState(0);

    const headerRef = useRef(header);
    useEffect(() => {
        headerRef.current = header;
    });

    useEffect(() => {
        if (!organizationId) return undefined;
        let current = true;
        organizationApi.getLanguages(organizationId, headerRef.current).then(
            (settings) => {
                if (current) {
                    setRead({ organizationId, settings, failed: false });
                }
            },
            // Quiet: the screen decides whether to say anything.
            () => {
                if (current) {
                    setRead({ organizationId, settings: null, failed: true });
                }
            }
        );
        return () => {
            current = false;
        };
    }, [organizationId, attempt]);

    const retry = useCallback(() => {
        // The failure is forgotten while the new answer is on its way.
        setRead((previous) => (previous?.failed ? null : previous));
        setAttempt((count) => count + 1);
    }, []);

    const mine =
        read && organizationId && read.organizationId === organizationId
            ? read
            : null;
    return {
        settings: mine?.settings ?? null,
        failed: mine?.failed ?? false,
        retry,
    };
}

/**
 * An organisation's language settings, read once for whoever needs them: the
 * languages it offers, the one it starts in, whether students see AI text nobody
 * has reviewed yet, and what the caller may change (`GET
 * /organization/:id/languages`). It is the whole answer, or null while the
 * answer is on its way and for good when it cannot be read, so a screen shows
 * nothing rather than a guess. The failure is quiet: the screen decides whether
 * to say anything (`useOrganizationLanguagesRead` says whether it failed).
 *
 * What was read belongs to one organisation: another one starts from nothing,
 * and an answer that arrives for one no longer asked about is dropped. With no
 * organisation (null, undefined or empty) nothing is read, which is also how a
 * caller that already holds what it needs asks for nothing. `header` is read
 * when the request goes out and is not a dependency, so a caller that builds it
 * inline does not ask again on every render.
 */
export function useOrganizationLanguages(
    organizationId: string | null | undefined,
    header: any
): OrganizationLanguages | null {
    return useOrganizationLanguagesRead(organizationId, header).settings;
}
